// WHY (#2140): map a frozen `RequestReturnSnapshotPayload` (#2135) onto the
// existing `RequestView` so the read-only display components (ItemsTab,
// RequestBoxesView, CarrierBidCard) render a return snapshot verbatim. Every
// non-frozen field is a safe placeholder: the snapshot is presentation-only —
// no party identity, no chat/audit/attachments, no funnel links, no
// financials. `logistId`/`managerId` are intentionally empty so the regroup
// party gate can never match (defence-in-depth; snapshot boxes also carry no
// `updatedAt` version token, so `canRegroupBoxItems` already returns false).

import type { RequestReturnSnapshotPayload } from '@magimp/shared';
import {
  CARRIER_STATE_MAP,
  isoToEpoch,
  wireBidToQuote,
  wireBoxToView,
  wireItemToView,
} from './adapter.js';
import type { CarrierBid, RequestView, UserRef } from './types.js';

const FROZEN_PARTY: UserRef = { id: '', name: '', role: 'manager' };

export function snapshotPayloadToView(payload: RequestReturnSnapshotPayload): RequestView {
  const { header } = payload;
  const createdAt = isoToEpoch(header.createdAt);
  return {
    id: header.id,
    name: header.name,
    status: header.status,
    kind: header.kind,
    // INVARIANT (#2140): empty party ids — a frozen snapshot exposes no
    //   write authority, and the empty `logistId` keeps the regroup party
    //   gate from ever matching.
    managerId: '',
    logistId: '',
    manager: FROZEN_PARTY,
    logist: null,
    createdAt,
    updatedAt: createdAt,
    slaDeadline: null,
    inWorkAt: null,
    closedAt: null,
    processedAt: null,
    route: { from: header.routeFrom, to: header.routeTo },
    destinations: [],
    deliveryAddress: header.deliveryAddress,
    // INVARIANT (#2580): frozen views use the snapshot's own origin and never
    //   fall back to the live request row.
    pickupAddress: header.pickupAddress,
    insuranceRequested: false,
    weight: header.weight,
    volume: header.volume,
    items: payload.items.map(wireItemToView),
    boxes: payload.boxes.map(wireBoxToView),
    carriers: payload.carriers.map(
      (c): CarrierBid => ({
        id: c.id,
        // WHY: the slim snapshot carrier carries no catalog `code`/`country`;
        //   the badge UI derives initials from `name` (see `carrierMarkText`).
        carrier: { id: c.carrierId, name: c.carrierName, code: '', country: '' },
        arrivalCityId: c.arrivalCityId,
        state: CARRIER_STATE_MAP[c.state],
        bids: c.bids.map(wireBidToQuote),
        // WHY (#2135): snapshot carriers omit `insuranceQuotes` by design.
        rejectReason: c.rejectReason,
        createdAt: isoToEpoch(c.createdAt),
        updatedAt: isoToEpoch(c.updatedAt),
      }),
    ),
    winnerBidId: null,
    attachments: [],
    chat: [],
    audit: [],
    returnReason: null,
    refusalReason: null,
    refusalComment: null,
    archivedFromStatus: null,
  };
}
